<template>
  <main class="lobby bg-cw-orange">
    <transition name="screen" mode="out-in">
      <GameLobbyPhase v-if="gamePhase == 'GAME_LOBBY_PHASE'" />
      <GamePlayPhase v-else-if="gamePhase == 'GAME_PLAY_PHASE'" />
      <GameLearningPhase v-else-if="gamePhase == 'GAME_LEARNING_PHASE'" />
      <GameSettingsPhase v-else-if="gamePhase == 'GAME_SETTINGS_PHASE'" />
      <GameLeaderboardPhase v-else-if="gamePhase == 'GAME_LEADERBOARD_PHASE'" />
      <GameEndedPhase v-else-if="gamePhase == 'GAME_ENDED_PHASE'" />
    </transition>

    <ConnectionStatus :timeRemaining="userPlayerProfile ? userPlayerProfile.reconnectTimeout : undefined" @timeUp="endGame" />
    <DisabledStatus v-if="isDisabled" />
    <GameActionLoader v-if="appStore.isShowLoader" />
  </main>
</template>

<script setup lang="ts">
import GamePlayPhase from '@/components/phases/GamePlayPhase.vue'
import GameLobbyPhase from '@/components/phases/GameLobbyPhase.vue'
import GameLearningPhase from '@/components/phases/GameLearningPhase.vue'
import GameSettingsPhase from '@/components/phases/GameSettingsPhase.vue'
import GameLeaderboardPhase from '@/components/phases/GameLeaderboardPhase.vue'
import GameActionLoader from '@/components/interface/GameActionLoader.vue'
import ConnectionStatus from '@/components/interface/ConnectionStatus.vue'
import DisabledStatus from '@/components/interface/DisabledStatus.vue'

import { useSocketEvents } from '@/composables/useEvents'
import { computed, onBeforeUnmount, provide } from 'vue'
import { useAppStore } from '@/stores/app'
import { useGameStore } from '@/stores/game'
import useSocket from '@/composables/useSocket'
import { useUserStore } from '@/stores/user'
import { useUser } from '@/composables/useUser'
import { storeToRefs } from 'pinia'

import GameEndedPhase from '@/components/phases/GameEndedPhase.vue'
import { AppWindowEvent, GameEvent } from '@/types/Events'
import { useHelpers } from '@/composables/useHelpers'
import { readStorage } from '@/composables/useStorage'

const appStore = useAppStore()
const { gamePhase, userPlayerProfile } = storeToRefs(useGameStore())
const { setUser } = useUserStore()
const socket = useSocket()
const { fetchUserProfile } = useUser()
const { showLoader, hideLoader } = useAppStore()
const { updateGameState } = useGameStore()
const { resetAppState } = useAppStore()
const { sleep, printEvent, sendParentWindowMessage } = useHelpers()

const isDisabled = computed(() => appStore.isDisabled)

const initializeGame = () => {
  fetchUserProfile()
  const userCache = readStorage('session', 'user')
  setUser(userCache ? JSON.parse(userCache) : null)
  useSocketEvents()
}

const endGame = async () => {
  try {
    await socket.sendEventAsync(GameEvent.LeaveGame)
    updateGameState(null)
    await sleep(200)
    sendParentWindowMessage(AppWindowEvent.ExitGame)
    resetAppState()
  } catch (error) {
    printEvent('Failed to leave game:', error, 'error')
  }
}

initializeGame()

onBeforeUnmount(() => {
  socket.disconnect()
})

provide('showLoader', showLoader)
provide('hideLoader', hideLoader)
</script>
